import { z } from 'zod';
import {
  tenantThemeSchema,
  THEME_RADIUS_CSS,
  THEME_FONT_CSS,
  THEME_BACKGROUND_CSS,
  THEME_CARD_CSS,
  THEME_BACKGROUND_STYLE_CSS,
} from './tenant-site';

// Kontrak branding per-tenant (dok desain 05 §1b + ekstensi analytics/support_email).
// Disimpan sebagai tenants.branding_json di control plane; dikonsumsi API worker,
// Pages Functions middleware (inject <head>), dan web app (useTenant()).

export const tenantBrandingSchema = z.object({
  brand_name: z.string().min(1),
  logo_url: z.string().url().optional(),
  favicon_url: z.string().url().optional(),
  colors: z
    .object({
      primary: z.string().regex(/^#[0-9a-fA-F]{6}$/),
      accent: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    })
    .default({ primary: '#0F6E56' }),
  email: z
    .object({
      sender_name: z.string().optional(),
      sender_address: z.string().email().optional(),
      // true hanya setelah domain sender diverifikasi di Mailketing (prasyarat onboarding);
      // false → pengirim fallback platform dipakai. (Jalur legacy platform-managed.)
      verified: z.boolean().default(false),
      // ── White-label sender domain-sendiri (self-service tenant) ──────────────
      // Domain pengirim milik tenant (mis. "sekolahku.id" / "mail.sekolahku.id").
      // Diisi saat tenant meminta domain sender sendiri; sumber runtime utamanya
      // adalah app_settings key 'email_sender_domain' (lihat emailSenderDomainSchema),
      // field di sini adalah cermin kontrak branding + jalur platform-managed.
      custom_domain: z.string().optional(),
      // true HANYA setelah DNS (SPF+DKIM) terverifikasi. Sender domain-sendiri
      // dipakai untuk From: email tenant hanya bila flag ini true; else fallback platform.
      dkim_verified: z.boolean().optional(),
      // Record DNS yang harus dipasang tenant di pengelola DNS domainnya.
      verification_records: z
        .array(
          z.object({
            type: z.enum(['TXT', 'CNAME', 'MX']),
            name: z.string(),
            value: z.string(),
            purpose: z.enum(['spf', 'dkim', 'dmarc']).optional(),
          })
        )
        .optional(),
    })
    .default({ verified: false }),
  landing: z
    .object({
      tagline: z.string().optional(),
      og_image_url: z.string().url().optional(),
    })
    .default({}),
  analytics: z
    .object({
      ga_id: z.string().optional(),
      /** Meta (Facebook) Pixel id — di-inject web bila terisi. */
      meta_pixel_id: z.string().regex(/^[A-Za-z0-9_-]*$/).optional(),
      /** Meta Conversions API access token — ENCRYPTED di DB, untuk server-side tracking. */
      meta_capi_token: z.string().optional(),
      /** TikTok Pixel id — di-inject web bila terisi. */
      tiktok_pixel_id: z.string().regex(/^[A-Za-z0-9_-]*$/).optional(),
    })
    .default({}),
  support_email: z.string().email().optional(),
  /** Tema tampilan (radius/font/background) — dipilih admin tenant; wajib optional+default agar branding_json lama tetap lolos parse. */
  theme: tenantThemeSchema.default({}),
  /** SEO global tenant — dipakai edge rewriter untuk <title>/meta description. */
  seo: z
    .object({
      meta_title_suffix: z.string().optional(),
      meta_description: z.string().optional(),
    })
    .default({}),
});

export type TenantBranding = z.infer<typeof tenantBrandingSchema>;

// ── Sender domain-sendiri (white-label email) — store self-service tenant ────
// Disimpan di app_settings tenant (key: 'email_sender_domain'), BUKAN di control
// plane branding_json — konsisten dengan config self-service lain (business_profile
// dkk) dan bekerja di mode single maupun multi. resolveTenantSender() memakainya
// sebagai From: email tenant HANYA bila dkim_verified === true; else fallback ke
// pengirim platform (no-reply@growclass.id).

/** Satu record DNS yang harus dipasang tenant (SPF/DKIM/DMARC). */
export const emailDnsRecordSchema = z.object({
  type: z.enum(['TXT', 'CNAME', 'MX']),
  /** Nama/host record (mis. "sekolahku.id", "mk._domainkey.sekolahku.id"). */
  name: z.string(),
  /** Nilai record (mis. "v=spf1 include:mailketing.co.id ~all"). */
  value: z.string(),
  /** Fungsi record — untuk label UI & logika verifikasi. */
  purpose: z.enum(['spf', 'dkim', 'dmarc']).optional(),
});

export type EmailDnsRecord = z.infer<typeof emailDnsRecordSchema>;

export const emailSenderDomainSchema = z.object({
  /** Domain pengirim milik tenant (mis. "sekolahku.id" / "mail.sekolahku.id"). */
  domain: z.string().min(3).max(253),
  /** Local-part alamat pengirim; From: = `${from_local}@${domain}`. */
  from_local: z.string().min(1).max(64).default('no-reply'),
  /** Nama tampilan pengirim; absen = nama brand tenant. */
  sender_name: z.string().max(80).optional(),
  /** true HANYA setelah SPF+DKIM terverifikasi via DNS lookup. Guard sender-switch. */
  dkim_verified: z.boolean().default(false),
  /** Record DNS yang di-generate untuk ditampilkan & disalin tenant. */
  records: z.array(emailDnsRecordSchema).default([]),
  /** ISO — saat domain diminta. */
  requested_at: z.string().optional(),
  /** ISO — saat verifikasi terakhir sukses (dkim_verified jadi true). */
  verified_at: z.string().optional(),
  /** ISO — pengecekan verifikasi terakhir (sukses/gagal). */
  last_checked_at: z.string().optional(),
});

export type EmailSenderDomain = z.infer<typeof emailSenderDomainSchema>;

/** Alamat pengirim lengkap dari config domain-sendiri, atau null bila belum siap. */
export function emailSenderAddress(config: EmailSenderDomain | null | undefined): string | null {
  if (!config || !config.domain || !config.from_local) return null;
  return `${config.from_local}@${config.domain}`.toLowerCase();
}

export const DEFAULT_BRANDING: TenantBranding = tenantBrandingSchema.parse({
  brand_name: 'growclass',
  colors: { primary: '#0F6E56' },
});

export function parseTenantBranding(json: unknown): TenantBranding {
  const raw = typeof json === 'string' ? safeJsonParse(json) : json;
  const result = tenantBrandingSchema.safeParse(raw ?? {});
  if (result.success) return result.data;
  return DEFAULT_BRANDING;
}

function safeJsonParse(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

// globals.css memakai --primary sebagai triplet HSL ("160 76% 28%"); kontrak colors = hex.
export function hexToHslTriplet(hex: string): string {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return '160 76% 28%';
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 0xff) / 255;
  const g = ((n >> 8) & 0xff) / 255;
  const b = (n & 0xff) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Warna teks kontras untuk background hex — luminance YIQ, threshold 128
 * (putih di background gelap, #111827 di background terang). Hex invalid →
 * '#FFFFFF' (asumsi gelap). Logika sama dengan contrastTextColor di
 * apps/api/src/lib/email-templates.ts (email inline tetap punya salinannya sendiri).
 */
export function contrastTextColor(hex: string): '#FFFFFF' | '#111827' {
  return yiqLuminance(hex) >= 128 ? '#111827' : '#FFFFFF';
}

/** Luminance YIQ 0–255. Hex invalid → 0 (asumsi gelap, konsisten dgn contrastTextColor). */
function yiqLuminance(hex: string): number {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return 0;
  const n = parseInt(m[1], 16);
  return (((n >> 16) & 0xff) * 299 + ((n >> 8) & 0xff) * 587 + (n & 0xff) * 114) / 1000;
}

function srgbChannelToLinear(c: number): number {
  const cs = c / 255;
  return cs <= 0.03928 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4);
}

/** Relative luminance WCAG (0–1). Hex invalid → 0. */
function relativeLuminance(hex: string): number {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return 0;
  const n = parseInt(m[1], 16);
  const r = srgbChannelToLinear((n >> 16) & 0xff);
  const g = srgbChannelToLinear((n >> 8) & 0xff);
  const b = srgbChannelToLinear(n & 0xff);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Rasio kontras WCAG asli antara dua warna hex (1–21). BEDA dari
 * contrastTextColor/yiqLuminance di atas — itu heuristik cepat (YIQ, ambang
 * 128) untuk memilih teks putih/hitam saat render; fungsi ini menghitung
 * angka rasio sungguhan untuk MEMVALIDASI pasangan warna (mis. admin memilih
 * warna brand) terhadap ambang WCAG 4.5:1 (AA, teks normal) — bukan pengganti,
 * keduanya dipakai untuk tujuan berbeda.
 */
export function contrastRatio(hexA: string, hexB: string): number {
  const lA = relativeLuminance(hexA);
  const lB = relativeLuminance(hexB);
  const lighter = Math.max(lA, lB);
  const darker = Math.min(lA, lB);
  return (lighter + 0.05) / (darker + 0.05);
}

/** true bila pasangan warna lolos WCAG AA (4.5:1 teks normal, 3:1 teks besar/bold ≥18px). */
export function meetsWcagAA(hexA: string, hexB: string, largeText = false): boolean {
  return contrastRatio(hexA, hexB) >= (largeText ? 3 : 4.5);
}

// CSS yang di-inject middleware Pages ke <head> tiap response HTML tenant.
// Turunan hover/soft via color-mix ≈ palette legacy (#0a5a45 / #E8F5F0);
// default build statis tetap memakai hex legacy persis di globals.css.
// theme.intensity mengatur seberapa jauh warna brand merembes:
// - subtle (default/absen): TANPA token tambahan — output identik pra-intensity.
// - balanced: secondary/accent/bento-green/sidebar/checklist/progress/notif ke arah brand.
// - bold: seperti balanced + sidebar aktif solid brand + tint bento lain.
// Hex default bento diverifikasi dari apps/web/src/app/globals.css:
// blue #EFF6FF, purple #F5F3FF, orange #FFF7ED, rose #FFF1F2.
export function buildBrandCssVars(branding: TenantBranding): string {
  const primary = branding.colors.primary;
  const accent = branding.colors.accent ?? primary;
  const onPrimary = contrastTextColor(primary);
  const isLightPrimary = onPrimary === '#111827';
  // Hover TETAP menggelap seperti sebelumnya (niat desain lama: #0F6E56 → ±#0a5a45).
  // Yang diperbaiki hanya kasus ekstrem: brand yang sudah nyaris hitam tidak bisa
  // digelapkan lagi — color-mix(#000 80%, black) = #000, alias tanpa state hover
  // sama sekali. Di bawah ambang itu, hover dibalik jadi mencerah.
  const TOO_DARK_TO_DARKEN = 40; // YIQ; #0F6E56 ≈ 79 → tetap menggelap
  const hoverMix =
    yiqLuminance(primary) < TOO_DARK_TO_DARKEN
      ? `color-mix(in srgb, ${primary} 78%, white)`
      : `color-mix(in srgb, ${primary} 80%, black)`;
  const parts = [
    // Selector `:root:root` (specificity 0,2,0) SENGAJA lebih tinggi dari `:root`
    // (0,1,0) default di globals.css. Bundle CSS Next di-load SETELAH <style
    // id="tenant-theme"> di <head>, jadi kalau sama-sama `:root`, default hijau
    // menang urutan → warna tenant terabaikan. Specificity ini membuat warna
    // tenant menang tanpa bergantung urutan sumber (robust ke reorder Next).
    ':root:root{',
    `--brand-primary:${primary};`,
    `--brand-primary-hover:${hoverMix};`,
    `--brand-primary-soft:color-mix(in srgb, ${primary} 10%, white);`,
    // Teks/ikon di ATAS --brand-primary. Wajib dipakai menggantikan `text-white`
    // yang di-hardcode: tenant dengan primary terang (mis. #FFEB3B) sebelumnya
    // dapat putih-di-atas-kuning (rasio ~1.07:1).
    `--brand-on-primary:${onPrimary};`,
    // Alias shadcn — globals.css memberi nilai statis #ffffff, di sini ditimpa
    // supaya `text-primary-foreground` ikut kontras.
    `--primary-foreground:${onPrimary};`,
    // Shade cerah untuk aksen dark-mode (menggantikan hardcode emerald
    // #10B981/#34D399 yang dulu tak ikut rebrand).
    `--brand-primary-bright:color-mix(in srgb, ${primary} 72%, white);`,
    `--brand-primary-brighter:color-mix(in srgb, ${primary} 55%, white);`,
    `--brand-accent:${accent};`,
    `--primary:${hexToHslTriplet(primary)};`,
  ];
  if (branding.theme.radius) parts.push(`--radius:${THEME_RADIUS_CSS[branding.theme.radius]};`);
  // Absen (tenant belum pernah pilih tema) sengaja tak menulis var sama sekali —
  // fallback di tiap komponen section (var(--card-shadow, <nilai asli komponen>))
  // menjaga tampilan identik dengan sebelum fitur ini ada.
  if (branding.theme.card_style) {
    const card = THEME_CARD_CSS[branding.theme.card_style];
    parts.push(
      `--card-shadow:${card.shadow};`,
      `--card-border-color:${card.borderColor};`,
      `--card-backdrop:${card.backdrop};`
    );
  }
  // Font kustom (font_url+font_family) menang atas preset; sanitasi nama family
  // (hanya alnum/spasi/-/_) untuk mencegah CSS-breakout.
  const customFontFamily =
    branding.theme.font_url && branding.theme.font_family
      ? branding.theme.font_family.replace(/[^a-zA-Z0-9 _-]/g, '').trim()
      : '';
  if (customFontFamily) {
    parts.push(`--brand-font:'${customFontFamily}', system-ui, sans-serif;`);
  } else if (branding.theme.font) {
    parts.push(`--brand-font:${THEME_FONT_CSS[branding.theme.font]};`);
  }
  if (branding.theme.background && branding.theme.background !== 'default')
    parts.push(`--surface:${THEME_BACKGROUND_CSS[branding.theme.background]};`);
  // Layer dekoratif TERPISAH dari --surface (lihat catatan di schema) — hanya
  // di-emit saat eksplisit & bukan 'flat', sama pola dengan card_style di atas.
  if (branding.theme.background_style && branding.theme.background_style !== 'flat') {
    const bg = THEME_BACKGROUND_STYLE_CSS[branding.theme.background_style];
    parts.push(`--surface-bg-image:${bg.image};`);
    if (bg.size) parts.push(`--surface-bg-size:${bg.size};`);
  }
  // Default 'balanced' (bukan 'subtle'): warna brand menyebar ke sidebar aktif,
  // checklist, progress, bento, dan secondary — bukan hanya tombol & tautan.
  // Tenant yang belum memilih intensity eksplisit ikut default ini.
  const intensity = branding.theme.intensity ?? 'balanced';
  if (intensity === 'balanced' || intensity === 'bold') {
    parts.push(
      `--secondary:color-mix(in srgb, ${primary} ${intensity === 'bold' ? 8 : 5}%, white);`,
      `--accent:color-mix(in srgb, ${primary} 6%, white);`,
      '--bento-green:var(--brand-primary-soft);',
      '--bento-green-icon:var(--brand-primary);',
      '--checklist-accent:var(--brand-primary);',
      '--progress-fill:var(--brand-primary);',
      '--notif-accent:var(--brand-primary);'
    );
    if (intensity === 'bold') {
      parts.push(
        '--sidebar-active-bg:var(--brand-primary);',
        `--sidebar-active-fg:${contrastTextColor(primary)};`,
        `--bento-blue:color-mix(in srgb, ${primary} 4%, #EFF6FF);`,
        `--bento-purple:color-mix(in srgb, ${primary} 4%, #F5F3FF);`,
        `--bento-orange:color-mix(in srgb, ${primary} 4%, #FFF7ED);`,
        `--bento-rose:color-mix(in srgb, ${primary} 4%, #FFF1F2);`
      );
    } else {
      parts.push(
        `--sidebar-active-bg:color-mix(in srgb, ${primary} 10%, white);`,
        // Brand terang di atas tint 10%-nya sendiri tidak terbaca (mis. primary
        // #FFEB3B → kuning di atas nyaris-putih), jadi digelapkan dulu.
        isLightPrimary
          ? `--sidebar-active-fg:color-mix(in srgb, ${primary} 55%, black);`
          : '--sidebar-active-fg:var(--brand-primary);'
      );
    }
  }
  parts.push('}');
  const root = parts.join('');
  // Prepend @font-face (di luar :root) untuk font kustom. font_url sudah tervalidasi
  // URL oleh zod; buang karakter penutup url() untuk keamanan ekstra.
  if (customFontFamily && branding.theme.font_url) {
    const safeUrl = branding.theme.font_url.replace(/['")]/g, '');
    return `@font-face{font-family:'${customFontFamily}';src:url('${safeUrl}');font-display:swap;}${root}`;
  }
  return root;
}

// ── Palet panel ilustrasi halaman auth ───────────────────────────────────────

/** hex → {h,s,l} (derajat / persen). Hex invalid → null. */
function hexToHsl(hex: string): { h: number; s: number; l: number } | null {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 0xff) / 255;
  const g = ((n >> 8) & 0xff) / 255;
  const b = (n & 0xff) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
}

/** h derajat (boleh negatif/lebih dari 360 — dinormalisasi), s/l persen. */
function hslToHex(h: number, s: number, l: number): string {
  const hn = ((h % 360) + 360) % 360;
  const sn = Math.min(100, Math.max(0, s)) / 100;
  const ln = Math.min(100, Math.max(0, l)) / 100;
  const c = (1 - Math.abs(2 * ln - 1)) * sn;
  const x = c * (1 - Math.abs(((hn / 60) % 2) - 1));
  const m = ln - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (hn < 60) [r, g, b] = [c, x, 0];
  else if (hn < 120) [r, g, b] = [x, c, 0];
  else if (hn < 180) [r, g, b] = [0, c, x];
  else if (hn < 240) [r, g, b] = [0, x, c];
  else if (hn < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const toHex = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

/**
 * Palet deterministik ilustrasi panel auth, diturunkan dari warna primary tenant.
 * - bg: primary digelapkan (L × 0.7, min 12 — faktor 0.7 dipilih agar default
 *   #0F6E56 menghasilkan bg ≈ #0C4A38 sesuai kontrak; 0.45 terlalu gelap).
 * - charA/C/D: rotasi hue +90° / +150° / −150° dengan S/L tetap (kontras satu sama lain).
 * - charB: tinta gelap tetap #2D2D2D (netral di semua brand).
 * Hex invalid → fallback HSL primary default #0F6E56.
 */
export function buildAuthPanelPalette(primaryHex: string): {
  bg: string;
  charA: string;
  charB: string;
  charC: string;
  charD: string;
} {
  const hsl = hexToHsl(primaryHex) ?? { h: 164.9, s: 76, l: 24.5 };
  return {
    bg: hslToHex(hsl.h, hsl.s, Math.max(12, hsl.l * 0.7)),
    charA: hslToHex(hsl.h + 90, 75, 60),
    charB: '#2D2D2D',
    charC: hslToHex(hsl.h + 150, 65, 70),
    charD: hslToHex(hsl.h - 150, 65, 62),
  };
}
